/**
 * SermonsPublicPage — the public teaching library at /media, in the FWM
 * house style: a full-bleed hero from the hero registry, then the archive
 * as photograph-led cards on the cream ground.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowRight, Headphones, Play, Radio, Search } from 'lucide-react';
import FwmShell from '@/components/fwm/FwmShell';
import { FwmHero } from '@/components/fwm/FwmHero';
import { useHero } from '@/components/fwm/useHero';
import { FWM_IMAGERY, FWM_CARD_ART, resolveImage } from '@/components/fwm/imagery';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const minutes = (seconds) => (seconds ? `${Math.round(seconds / 60)} min` : '');

const dateLine = (value) => (value
  ? new Date(value).toLocaleDateString(undefined,
    { day: 'numeric', month: 'short', year: 'numeric' })
  : '');

const SermonCard = ({ sermon, index, onOpen }) => {
  const id = sermon.id || sermon.slug;
  const art = resolveImage(sermon.thumbnail_url || sermon.image_url)
    || FWM_CARD_ART[index % FWM_CARD_ART.length];
  const overline = [dateLine(sermon.publication_date), minutes(sermon.duration_seconds)]
    .filter(Boolean).join(' · ');
  return (
    <article className="fwm-photo-card fwm-rise" data-testid={`sermon-${id}`}
             style={{ animationDelay: `${index * 60}ms` }}>
      <button type="button" className="fwm-photo-card-hit" data-testid={`sermon-open-${id}`}
              onClick={() => onOpen(sermon)}>
        <span className="fwm-photo-card-media">
          <img src={art} alt={sermon.title} loading="lazy" />
          {overline && <span className="fwm-photo-card-overline">{overline}</span>}
          <span className="fwm-photo-card-badge">
            <Play className="h-3 w-3" /> {sermon.series_name || 'Teaching'}
          </span>
        </span>
        <span className="fwm-photo-card-body">
          <span className="fwm-photo-card-title">{sermon.title}</span>
          {sermon.speaker_name && (
            <span className="mt-2 text-xs uppercase tracking-[0.14em]
                             text-[color:var(--fwm-muted)]">{sermon.speaker_name}</span>
          )}
          {sermon.description && (
            <span className="fwm-photo-card-blurb">{sermon.description}</span>
          )}
          <span className="fwm-photo-card-cta">
            Open the message <ArrowRight className="h-3.5 w-3.5" />
          </span>
        </span>
      </button>
      <div className="flex gap-3 px-6 pb-6">
        {sermon.video_url && (
          <a href={sermon.video_url} target="_blank" rel="noreferrer"
             className="fwm-btn !py-2 !px-4 !text-[0.75rem]"
             data-testid={`sermon-watch-${id}`}>
            <Play className="h-3.5 w-3.5" /> Watch
          </a>
        )}
        {sermon.audio_url && (
          <a href={sermon.audio_url} target="_blank" rel="noreferrer"
             className="fwm-btn-ghost !py-2 !px-4 !text-[0.75rem]"
             data-testid={`sermon-listen-${id}`}>
            <Headphones className="h-3.5 w-3.5" /> Listen
          </a>
        )}
      </div>
    </article>
  );
};

export default function SermonsPublicPage() {
  const navigate = useNavigate();
  const [term, setTerm] = useState('');
  const [q, setQ] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => setQ(term), 350);
    return () => clearTimeout(timer);
  }, [term]);

  const { data, isLoading } = useQuery({
    queryKey: ['public-sermons', q],
    queryFn: () => fetch(`${API_URL}/api/fwtv/public/sermons?limit=48${
      q ? `&q=${encodeURIComponent(q)}` : ''}`).then((r) => r.json()),
  });
  const sermons = data?.sermons || [];
  const hero = useHero('media', { image_url: FWM_IMAGERY.teaching });

  return (
    <FwmShell ctaLabel="Faith Works TV" ctaLink="/tv" testId="sermons-page" overDark>
      <FwmHero
        testId="media-hero"
        image={hero.image_url}
        fallbackImage={hero.fallback_image_url}
        video={hero.video_url}
        mediaPriority={hero.media_priority}
        eyebrow={hero.eyebrow}
        title={hero.title}
        accent={hero.accent}
        blurb={hero.blurb}
        actions={(
          <>
            <button className="fwm-btn fwm-btn-lg" data-testid="sermons-browse-btn"
                    onClick={() => document.getElementById('archive')
                      ?.scrollIntoView({ behavior: 'smooth' })}>
              Browse the archive <ArrowRight className="h-4 w-4" />
            </button>
            <button className="fwm-btn-ondark" data-testid="sermons-tv-link"
                    onClick={() => navigate('/tv')}>
              <Radio className="h-4 w-4" /> Faith Works TV
            </button>
          </>
        )}
      />

      <section id="archive" className="fwm-section" data-testid="sermons-archive">
        <div className="fwm-section-head">
          <div>
            <p className="fwm-kicker">Teaching archive</p>
            <h2 className="mt-3">{q ? `Messages matching "${q}"` : 'Every message, kept'}</h2>
          </div>
          <div className="relative w-full sm:w-72">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5
                               text-[color:var(--fwm-muted)]" />
            <input value={term} onChange={(e) => setTerm(e.target.value)}
                   placeholder="Search teaching" data-testid="sermons-search"
                   className="fwm-field !pl-9" />
          </div>
        </div>

        {isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-80 rounded-xl bg-[color:var(--fwm-ground-2)] animate-pulse" />
            ))}
          </div>
        ) : sermons.length === 0 ? (
          <div className="border border-[color:var(--fwm-line)] rounded-xl px-8 py-16 text-center"
               data-testid="sermons-empty">
            <Headphones className="h-8 w-8 mx-auto text-[color:var(--fwm-muted)]" />
            <p className="mt-5 text-[color:var(--fwm-body)]">
              Nothing has been published here yet — messages appear the moment they are released.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
               data-testid="sermons-grid">
            {sermons.map((s, i) => (
              <SermonCard key={s.id || s.slug} sermon={s} index={i}
                          onOpen={(item) => navigate(`/media/${item.slug || item.id}`)} />
            ))}
          </div>
        )}
      </section>

      <section className="border-t border-[color:var(--fwm-line)]">
        <div className="fwm-section !py-20 flex flex-col lg:flex-row lg:items-center
                        justify-between gap-10">
          <div className="max-w-xl">
            <h2 className="fwm-display text-3xl sm:text-4xl tracking-tight">
              Watch the whole library
            </h2>
            <p className="mt-4 text-[color:var(--fwm-body)] leading-relaxed">
              Live services, teaching series and courses stream on Faith Works TV — free to
              watch, with the full archive open to subscribers.
            </p>
          </div>
          <button className="fwm-btn fwm-btn-lg shrink-0" data-testid="sermons-tv-cta"
                  onClick={() => navigate('/tv')}>
            Open Faith Works TV <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      </section>
    </FwmShell>
  );
}
